/**
 * THE VISITOR-FACING CONSENT SURFACE. Frames BC-01 – BC-14.
 *
 * Three surfaces, and the difference between the first two is the whole
 * argument:
 *
 *   `ConsentBanner`      a REGION. No scrim, no focus trap, no auto-focus. It
 *                        renders in the footer stack above the primary action —
 *                        the slot and the rule the app prompt already follows —
 *                        so someone who ignores it books an appointment exactly
 *                        as fast as someone who reads it. A modal would make an
 *                        optional request compulsory, which is the pattern that
 *                        produces consent nobody meant to give.
 *
 *   `CookieSettings`     a DIALOG, because the visitor opened it deliberately.
 *
 *   `ConsentFooterItems` the persistent way back, inside the page's single
 *                        legal/settings group. A decision you cannot revisit is
 *                        not a decision.
 *
 * NOTHING HERE RENDERS ON A CREDENTIAL ROUTE, and that is not this file's
 * judgement — `askable` is false there, and false again while Legal/Privacy has
 * not published the notice. Both checks live in the context so a second consent
 * surface cannot be built that forgets one.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHERE FOCUS GOES. Every path, named, because the one that was missing left
 * focus on `<body>` and nothing noticed.
 *
 *   Allow analytics (banner)          → the footer Cookie settings control
 *   Continue without analytics        → the footer Cookie settings control
 *   Save, dialog opened from banner   → the footer Cookie settings control
 *   Save, dialog opened from footer   → that control (it survives)
 *   Withdrawal (Save with Off)        → the footer Cookie settings control
 *   Already-off no-op Save            → the footer Cookie settings control
 *   Escape                            → the opener if it still exists, else footer
 *   Close                             → the opener if it still exists, else footer
 *   Scrim                             → the opener if it still exists, else footer
 *   Failed save                       → stays in the dialog, on Try again
 *
 * The rule underneath all of it: NEVER focus a detached node and never leave
 * focus on `<body>`. `focusAfterClose` checks `document.contains` rather than
 * trusting a captured ref, because the banner unmounts in the same commit as
 * the answer that dismisses it — which is exactly how the bug happened.
 */
import { useFocusOnMount } from "../../../../shared/hooks/useFocusOnMount";
import { useEffect, useId, useRef, useState } from "react";
import { useOptionalConsent, type ConsentApi } from "../analytics/ConsentContext";
import { useTranslation } from "../i18n/useTranslation";

/**
 * Focus the control that opened this, or the one that outlives it.
 *
 * A captured opener can be detached by the time a dialog closes — `.focus()`
 * on a detached node is a silent no-op and focus stays wherever it fell.
 */
function focusAfterClose(
  opener: HTMLElement | null | undefined,
  persistent: HTMLButtonElement | null
) {
  if (opener && document.contains(opener)) {
    opener.focus();
    return;
  }
  persistent?.focus();
}

/* ---------------------------------------------------------------- banner -- */

export function ConsentBanner() {
  const { t } = useTranslation();
  const consent = useOptionalConsent();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [failed, setFailed] = useState(false);
  const settingsLink = useRef<HTMLButtonElement>(null);
  const titleId = useId();

  if (!consent?.askable) return null;
  const { state, choose, persistentControl } = consent;

  // Asked once. `unknown` is the only undecided state; `unreadable` means we
  // could not read an answer, and asking again on every page load when storage
  // is unwritable would be a loop the visitor cannot escape.
  const undecided = state === "unknown";

  const answer = (choice: "granted" | "denied") => {
    if (!choose(choice, "banner")) {
      setFailed(true);
      return;
    }
    // The banner is about to unmount. Hand focus to the control that does not.
    persistentControl.current?.focus();
  };

  return (
    <>
      {undecided ? (
        <section
          className="b-consent"
          role="region"
          aria-labelledby={titleId}
          data-testid="consent-banner"
        >
          <div className="b-consent-b">
            <strong className="b-consent-t" id={titleId}>
              {t("consent.banner.title")}
            </strong>
            <p className="b-consent-p">{t("consent.banner.body")}</p>
            {/*
              EQUAL PROMINENCE. Same variant, same size, same width behaviour,
              adjacent in tab order. Neither is the accent button — the accent
              on this surface is the booking action — and neither is
              preselected or worded as the safe choice.
            */}
            <div className="b-consent-a">
              <button
                type="button"
                className="b-btn v-secondary s-sm"
                data-testid="consent-allow"
                onClick={() => answer("granted")}
              >
                {t("consent.action.allow")}
              </button>
              <button
                type="button"
                className="b-btn v-secondary s-sm"
                data-testid="consent-decline"
                onClick={() => answer("denied")}
              >
                {t("consent.action.decline")}
              </button>
            </div>
            {/*
              THE BANNER KEEPS ITS OWN LINKS even though the footer now carries
              a single legal group. Product's reason, and it is the right one:
              these are part of the consent request's context, not site
              navigation — the terms of the question being asked, beside the
              question.
            */}
            <p className="b-consent-links">
              <a href={consent.privacyNotice ?? undefined} data-testid="consent-privacy">
                {t("consent.link.privacy")}
              </a>
              <span aria-hidden="true">·</span>
              <button
                type="button"
                ref={settingsLink}
                className="b-linkbtn"
                data-testid="consent-open-settings"
                onClick={() => setSettingsOpen(true)}
              >
                {t("consent.link.settings")}
              </button>
            </p>
          </div>
        </section>
      ) : null}

      {failed ? <SaveFailure onRetry={() => setFailed(false)} /> : null}

      {settingsOpen ? (
        <CookieSettings
          onClose={() => setSettingsOpen(false)}
          opener={settingsLink}
          persistent={persistentControl}
        />
      ) : null}
    </>
  );
}

/* -------------------------------------------------- persistent controls -- */

/**
 * Privacy notice and Cookie settings, as items in the page's ONE legal group.
 *
 * Product resolved the duplicate-link question: the footer previously showed a
 * marketing "Privacy" link and a second "Privacy notice" link immediately
 * below it. There is now one group — Privacy notice · Cookie settings · Terms ·
 * Support — and this renders the first two into it.
 *
 * Both disappear together when there is no published notice, which is the
 * committed state. That is deliberate and it has a visible cost: until Legal
 * supplies the URL, the Book footer has no privacy link at all. A dead link,
 * or a guess at `uselura.com/privacy` that Legal has not approved for this
 * processing, would both be worse.
 */
export function ConsentFooterItems() {
  const { t } = useTranslation();
  const consent = useOptionalConsent();
  const [open, setOpen] = useState(false);

  // A settings confirmation belongs to the interaction that produced it. This
  // is what stops it surviving a navigation: the footer remounts per view.
  const clear = consent?.clearSettingsResult;
  useEffect(() => {
    clear?.();
  }, [clear]);

  if (!consent?.askable) return null;
  const { privacyNotice, persistentControl } = consent;

  return (
    <>
      <a href={privacyNotice ?? undefined} data-testid="consent-controls-privacy">
        {t("consent.link.privacy")}
      </a>
      <button
        type="button"
        ref={persistentControl}
        className="b-linkbtn"
        data-testid="consent-controls-settings"
        onClick={() => setOpen(true)}
      >
        {t("consent.link.settings")}
      </button>
      {open ? (
        <CookieSettings
          onClose={() => setOpen(false)}
          opener={persistentControl}
          persistent={persistentControl}
        />
      ) : null}
    </>
  );
}

/* ------------------------------------------------------- save failure -- */

function SaveFailure({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="b-notice t-danger" role="alert" data-testid="consent-save-failed">
      <div>
        <strong>{t("consent.failure.title")}</strong>
        {/* The true sentence, and the reason the write path returns a boolean:
            a preference we could not record must resolve toward not
            collecting, in either direction. */}
        <p>{t("consent.failure.body")}</p>
        <div className="b-notice-a">
          <button type="button" className="b-btn v-secondary s-sm" onClick={onRetry}>
            {t("consent.failure.action")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------ settings -- */

interface SettingsProps {
  onClose: () => void;
  opener: { current: HTMLElement | null };
  persistent: ConsentApi["persistentControl"];
}

/**
 * THE FOCUS AND KEYBOARD CONTRACT, because it is the part that is easy to get
 * wrong and invisible when it is wrong.
 *
 *   INITIAL FOCUS  the heading, `tabIndex={-1}` — never a radio. Focusing a
 *                  radio in a group MOVES THE SELECTION in some assistive
 *                  configurations, which would silently change the setting the
 *                  visitor came to read.
 *   ESCAPE         closes without saving. So does Close and the scrim.
 *                  BOUND AT THE DOCUMENT, not on the dialog element. A React
 *                  handler on `.b-sheet` only fires for events targeting inside
 *                  it, and clicking any non-focusable content — the setting
 *                  description, which is the text the visitor opened it to read
 *                  — leaves `activeElement` on `<body>`. Escape then never
 *                  reached the handler and the dialog would not close, in both
 *                  engines. QE found it.
 *   CLOSING        is not a decline. The previous answer is preserved exactly.
 *   FOCUS RETURN   see the table at the top of this file.
 *   CONTAINMENT    Tab and Shift+Tab cycle inside the dialog, INCLUDING when
 *                  focus is on the heading or has fallen to `<body>`.
 *   LABELLING      `aria-labelledby` the title, `aria-describedby` the setting
 *                  description. The choices are a `fieldset` whose `legend` is
 *                  the setting label, so each is announced in context.
 *   ERROR          `role="alert"`, and also the Save control's accessible
 *                  description, so it is announced when it appears and read
 *                  again when the visitor reaches Save.
 *   STATUS         a saved change announces through a `role="status"` region on
 *                  the page, not a toast that disappears before a screen reader
 *                  reaches it — and only for the interaction that produced it.
 */
export function CookieSettings({ onClose, opener, persistent }: SettingsProps) {
  const { t } = useTranslation();
  const consent = useOptionalConsent();
  const [pending, setPending] = useState<"granted" | "denied">(
    consent?.state === "granted" ? "granted" : "denied"
  );
  const [failed, setFailed] = useState(false);
  const dialog = useRef<HTMLDivElement>(null);
  const heading = useFocusOnMount();
  const save = useRef<HTMLButtonElement>(null);
  const titleId = useId();
  const descId = useId();
  const errorId = useId();

  /*
   * ESCAPE AND CONTAINMENT, BOTH AT THE DOCUMENT, AND BOTH SURVIVING FOCUS
   * LEAVING THE DIALOG.
   *
   * Anything that is not one of the dialog's own focusable elements wraps to
   * the appropriate end — which covers the `tabIndex={-1}` heading that takes
   * initial focus, `<body>`, and anything focus lands on behind the scrim.
   */
  /*
   * A ref, so the document listener does not need `dismiss` in its dependency
   * array. `dismiss` closes over props that change on every parent render, and
   * rebinding a global listener on every render is noise the effect does not
   * need.
   */
  const dismissRef = useRef<() => void>(() => {});

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        // The SAME path as Close and the scrim, focus return included. The
        // first version called `onClose()` directly here, so Escape closed the
        // dialog and left focus wherever it was — which on the path this whole
        // correction is about is `<body>`.
        dismissRef.current();
        return;
      }
      if (event.key !== "Tab") return;
      const container = dialog.current;
      if (!container) return;
      const focusable = [
        ...container.querySelectorAll<HTMLElement>(
          'button, [href], input, [tabindex]:not([tabindex="-1"])'
        ),
      ].filter((node) => !node.hasAttribute("disabled"));
      if (focusable.length === 0) return;
      const first = focusable[0] as HTMLElement;
      const last = focusable[focusable.length - 1] as HTMLElement;
      const active = document.activeElement as HTMLElement | null;
      const index = active ? focusable.indexOf(active) : -1;

      if (index === -1) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus();
        return;
      }
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, []);

  const dismiss = () => {
    // Escape, Close and the scrim. The opener may still be there; it may not.
    focusAfterClose(opener.current, persistent.current);
    onClose();
  };
  dismissRef.current = dismiss;

  const commit = () => {
    // Saving "off" while already off is a valid no-op and confirms; it must
    // not error and must not be treated as a change.
    if (!consent || !consent.choose(pending, "settings")) {
      setFailed(true);
      // Stay in the dialog, on the control that retries.
      save.current?.focus();
      return;
    }
    /*
     * A SAVE ALWAYS LANDS ON THE PERSISTENT CONTROL, not on the opener.
     *
     * Saving can change the answer, and changing the answer unmounts the
     * banner — including the link that opened this dialog. Focusing that link
     * "works" synchronously and then evaporates on the next commit, leaving
     * focus on `<body>`: the exact bug, reintroduced one line later. When the
     * dialog was opened from the footer the two are the same element anyway,
     * so this is not a special case, it is the rule.
     */
    persistent.current?.focus();
    onClose();
  };

  return (
    <div className="b-sheetwrap" data-testid="cookie-settings">
      <div className="b-scrim" onClick={dismiss} data-testid="cookie-settings-scrim" />
      <div
        className="b-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        ref={dialog}
      >
        <div className="b-sheet-top">
          <h2 className="b-h3" id={titleId} ref={heading} tabIndex={-1}>
            {t("consent.settings.title")}
          </h2>
          <button
            type="button"
            className="b-iconbtn"
            aria-label={t("consent.settings.close")}
            data-testid="cookie-settings-close"
            onClick={dismiss}
          >
            ×
          </button>
        </div>
        <div className="b-sheet-b">
          <fieldset className="b-fieldset">
            <legend className="b-h3">{t("consent.settings.label")}</legend>
            <p className="b-meta" id={descId}>
              {t("consent.settings.description")}
            </p>
            <div className="b-choices">
              {(
                [
                  ["granted", t("consent.settings.choiceOn"), "cookie-choice-on"],
                  ["denied", t("consent.settings.choiceOff"), "cookie-choice-off"],
                ] as const
              ).map(([value, label, testid]) => (
                <label key={value} className={`b-choice ${pending === value ? "is-sel" : ""}`}>
                  <input
                    type="radio"
                    name="optional-analytics"
                    value={value}
                    data-testid={testid}
                    checked={pending === value}
                    onChange={() => setPending(value)}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </fieldset>
          {failed ? (
            <div
              className="b-notice t-danger"
              role="alert"
              id={errorId}
              data-testid="cookie-settings-error"
            >
              <div>
                <strong>{t("consent.failure.title")}</strong>
                <p>{t("consent.failure.body")}</p>
              </div>
            </div>
          ) : null}
        </div>
        <div className="b-sheet-a">
          <button
            type="button"
            ref={save}
            className="b-btn v-primary is-full"
            data-testid="cookie-settings-save"
            aria-describedby={failed ? errorId : undefined}
            onClick={commit}
          >
            {failed ? t("consent.failure.action") : t("consent.settings.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------------- status -- */

/**
 * The saved confirmation — BC-06, and ONLY BC-06.
 *
 * Driven by the current settings interaction, never by stored state. A
 * first-time "Continue without analytics" (BC-03) records `denied` and shows
 * nothing: the visitor did not withdraw anything, and telling them they did is
 * both wrong and slightly alarming. Saving Off in the dialog — including the
 * approved already-off no-op — shows it for that interaction, and it does not
 * survive a navigation or a reload.
 */
export function ConsentStatus() {
  const { t } = useTranslation();
  const consent = useOptionalConsent();
  if (!consent?.askable || consent.settingsResult !== "denied") return null;
  return (
    <div className="b-notice t-quiet" role="status" data-testid="consent-status">
      <p>{t("consent.status.withdrawn")}</p>
    </div>
  );
}
